import { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import parse from "html-react-parser";
import { registerNlpStart } from '../../utils/utils';
import { useTranslation } from 'react-i18next';

export interface AnalyzedTextProps {
    task_id: string;
    analyzedText: any;
    saveFormattedTextCallback: CallableFunction;
    language?: string;
}

interface FilterItem {
    id: string;
    category: string;
    option: string;
    color: string;
    size: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
}

export default function AnalyzedTextEnglish(props: AnalyzedTextProps) {
    const { t } = useTranslation();

    const [allText, setAllText] = useState('');
    const [processingEmpty, setProcessingEmpty] = useState(false);
    const [nlpCategories, setNlpCategories] = useState<any>({});
    const [filters, setFilters] = useState<FilterItem[]>([]);
    const [showPicker, setShowPicker] = useState(false);
    const [expandedPickerGroup, setExpandedPickerGroup] = useState<string | null>(null);
    const [expandedPickerSubgroup, setExpandedPickerSubgroup] = useState<string | null>(null);

    // --- Frequency helpers (unchanged from original) ---

    const getTenseFrequency = (tenses: any, t: string) => {
        let value = 0;
        const index = tenses.findIndex((tense: any) => tense.tense == t);
        if (index !== -1) {
            value = tenses[index].frequency;
        }
        return value;
    }

    const getPragmaticFrequency = (pragmatics: any, p: string) => {
        let value = 0;
        const index = pragmatics.findIndex((pragmatic: any) => pragmatic.pragmatics == p);
        if (index !== -1) {
            value = pragmatics[index].frequency;
        }
        return value;
    }

    const getPhrasalFrequency = (phrasals: any, f: string) => {
        let value = 0;
        const index = phrasals.findIndex((phrasal: any) => phrasal.phrasalverbs == f);
        if (index !== -1) {
            value = phrasals[index].frequency;
        }
        return value;
    }

    // --- Color/Size decode ---

    function decodeColor(colorcode: string) {
        switch (colorcode) {
            case 'blue-violet': return 'blueviolet';
            case 'brown': return 'brown';
            case 'coral': return 'coral';
            case 'dark-blue': return 'darkblue';
            case 'dark-cyan': return 'darkcyan';
            case 'crimson': return 'crimson';
            case 'dark-green': return 'darkgreen';
            case 'dark-salmon': return 'darksalmon';
            case 'dark-orange': return 'darkorange';
            case 'cadet-blue': return 'cadetblue';
            case 'chocolate': return 'chocolate';
            case 'dark-magenta': return 'darkmagenta';
            default: return '';
        }
    }

    function decodeSize(sizecode: string) {
        switch (sizecode) {
            case 'big': return '1.4em';
            case 'huge': return '1.8em';
            default: return '';
        }
    }

    // --- Word matching ---

    const wordMatchesFilter = (word: any, filter: FilterItem): boolean => {
        switch (filter.category) {
            case 'pragmatics':
                if (word.pragmatics === '-') return false;
                switch (filter.option) {
                    case 'suggesting':
                        return ['Suggestion_Direct', 'Suggestion_Conventionalised', 'Suggestion_Indirect'].includes(word.pragmatics);
                    case 'thanking':
                        return ['Thanking_Informal', 'Thanking_Conventionalised', 'Thanking_Formal'].includes(word.pragmatics);
                    case 'requesting':
                        return ['Request_Direct', 'Request_Conventionalised', 'Request_Indirect'].includes(word.pragmatics);
                    case 'agreeing-disagreeing':
                        return ['Agreeing/Disagreeing_Informal', 'Agreeing/Disagreeing_Formal'].includes(word.pragmatics);
                    case 'showing-interest':
                        return ['Showing_Interest_Informal', 'Showing_Interest_Formal'].includes(word.pragmatics);
                    case 'apologising':
                        return ['Apologising_Conventionalised', 'Apologising_Informal', 'Apologising_Formal'].includes(word.pragmatics);
                    case 'giving-an-opinion':
                        return ['Giving_Opinion_Conventionalised', 'Giving_Opinion_Formal'].includes(word.pragmatics);
                    case 'greeting':
                        return ['Greeting_Informal', 'Greeting_Conventionalised', 'Greeting_Formal'].includes(word.pragmatics);
                    case 'all':
                    default:
                        return true;
                }

            case 'phrasalVerbs':
                return word.phrasalverb === 'PhrV';

            case 'nouns':
                if (!['NN', 'NNP', 'NNS', 'NNPS', 'IrrPl'].includes(word.xpos)) return false;
                if (filter.option === 'irregular') return word.irregularform === 'IrrPl';
                return true;

            case 'adjectives':
                if (!['JJ', 'JJR', 'JJS'].includes(word.xpos)) return false;
                if (filter.option === 'adjectives-comparative') return word.xpos === 'JJR';
                if (filter.option === 'adjectives-superlative') return word.xpos === 'JJS';
                return true;

            case 'adverbs':
                if (!['RB', 'RBR', 'RBS', 'WRB'].includes(word.xpos)) return false;
                if (filter.option === 'adverbs-comparative') return word.xpos === 'RBR';
                if (filter.option === 'adverbs-superlative') return word.xpos === 'RBS';
                if (filter.option === 'adverbs-wh') return word.xpos === 'WRB';
                return true;

            case 'verbs':
                if (!['VB', 'VBZ', 'VBD', 'VBN', 'MD', 'VBG', 'VBP'].includes(word.xpos)) return false;
                switch (filter.option) {
                    case 'verbs-irregular':
                        return word.irregularform === 'IrrV';
                    case 'verbs-modal':
                        return ['Modal_Verb', 'Modal_Perfect', 'Modal_Passive', 'Modal_Perfect_Passive'].includes(word.tense);
                    case 'verbs-imperative':
                        return word.tense === 'Imperative';
                    case 'verbs-phrasal':
                        return word.phrasalverb === 'PhrV';
                    case 'all':
                        return true;
                    default:
                        // Individual tense options: e.g. 'verbs-tense-Present_Simple'
                        if (filter.option.startsWith('verbs-tense-')) {
                            const tenseKey = filter.option.replace('verbs-tense-', '');
                            return word.tense === tenseKey;
                        }
                        return true;
                }

            case 'prepositions':
                return word.xpos === 'IN' && word.upos === 'ADP';

            case 'subConjunctions':
                return word.xpos === 'IN' && word.upos === 'SCONJ';

            case 'determiners':
                if (!['DT', 'PDT', 'WDT'].includes(word.xpos)) return false;
                if (filter.option === 'determiners-determiner') return ['DT', 'PDT'].includes(word.xpos);
                if (filter.option === 'determiners-wh') return word.xpos === 'WDT';
                return true;

            case 'pronouns':
                if (!['PRP', 'PRP$', 'WP$', 'WP'].includes(word.xpos)) return false;
                if (filter.option === 'pronouns-personal') return word.xpos === 'PRP';
                if (filter.option === 'pronouns-possessive') return word.xpos === 'PRP$';
                if (filter.option === 'pronouns-possessive-wh') return word.xpos === 'WP$';
                if (filter.option === 'pronouns-wh') return word.xpos === 'WP';
                return true;

            case 'conjunctions':
                return word.xpos === 'CC';

            default:
                return false;
        }
    }

    // --- Format text ---

    const formatText = (json: any, currentFilters: FilterItem[]) => {
        let resultAllText = '';
        if (json && json.length > 0) {
            let paragraphNum = json[0].paragraph;
            let sentenceNum = json[0].sentence;
            resultAllText = '<p>';
            let prevEndChar = 0;

            for (let i = 0; i < json.length; i++) {
                if (parseInt(json[i].sentence) != parseInt(sentenceNum)) {
                    resultAllText += ' ';
                    sentenceNum = json[i].sentence;
                }
                if (parseInt(json[i].paragraph) != parseInt(paragraphNum)) {
                    resultAllText += '</p><p>';
                    paragraphNum = json[i].paragraph;
                }
                if (prevEndChar != 0 && (prevEndChar < parseInt(json[i].start_char))) {
                    resultAllText += ' ';
                }

                // Find the last matching filter for this word
                let matchingFilter: FilterItem | null = null;
                for (const filter of currentFilters) {
                    if (wordMatchesFilter(json[i], filter)) {
                        matchingFilter = filter;
                    }
                }

                if (matchingFilter) {
                    let style = '';
                    let classProps = '';
                    if (matchingFilter.color) {
                        style += 'color:' + decodeColor(matchingFilter.color) + ';';
                    }
                    if (matchingFilter.size) {
                        style += 'font-size:' + decodeSize(matchingFilter.size) + ';';
                        classProps = 'text-' + matchingFilter.size;
                    }
                    if (matchingFilter.bold) {
                        style += 'font-weight: bold;';
                    }
                    if (matchingFilter.italic) {
                        style += 'font-style: italic;';
                    }
                    if (matchingFilter.underline) {
                        style += 'text-decoration: underline;';
                    }
                    resultAllText += '<span class="' + classProps + '" style="' + style + '">' + json[i].word + '</span>';
                } else {
                    resultAllText += json[i].word;
                }

                prevEndChar = json[i].end_char;
            }
            resultAllText += '</p>';
        }
        setAllText(resultAllText);
        props.saveFormattedTextCallback(resultAllText);
    }

    // --- Filter CRUD ---

    const addFilter = (category: string, option: string) => {
        const newFilter: FilterItem = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
            category,
            option,
            color: '',
            size: '',
            bold: false,
            italic: false,
            underline: false,
        };
        const newFilters = [...filters, newFilter];
        setFilters(newFilters);
        setShowPicker(false);
        setExpandedPickerGroup(null);
        setExpandedPickerSubgroup(null);
        registerNlpStart(props.task_id, 'Word Categories', category, option);
        formatText(props.analyzedText.main, newFilters);
    };

    const removeFilter = (id: string) => {
        const newFilters = filters.filter(f => f.id !== id);
        setFilters(newFilters);
        formatText(props.analyzedText.main, newFilters);
    };

    const updateFilter = (id: string, updates: Partial<FilterItem>) => {
        const newFilters = filters.map(f => f.id === id ? { ...f, ...updates } : f);
        setFilters(newFilters);
        formatText(props.analyzedText.main, newFilters);
    };

    const handleColorChange = (filterId: string, color: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Color', filter.category, color);
        }
        updateFilter(filterId, { color });
    };

    const handleSizeChange = (filterId: string, size: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Size', filter.category, size);
        }
        updateFilter(filterId, { size });
    };

    const handleBoldToggle = (filterId: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Bold', filter.category, !filter.bold ? 'yes' : 'no');
            updateFilter(filterId, { bold: !filter.bold });
        }
    };

    const handleItalicToggle = (filterId: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Italic', filter.category, !filter.italic ? 'yes' : 'no');
            updateFilter(filterId, { italic: !filter.italic });
        }
    };

    const handleUnderlineToggle = (filterId: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Underline', filter.category, !filter.underline ? 'yes' : 'no');
            updateFilter(filterId, { underline: !filter.underline });
        }
    };

    // --- Render helpers ---

    const colorOptions = (filterId: string, activeColor: string) => {
        const colors = ['blue-violet', 'brown', 'coral', 'dark-blue', 'dark-cyan', 'crimson',
            'dark-green', 'dark-salmon', 'dark-orange', 'cadet-blue', 'chocolate', 'dark-magenta'];
        return (
            <div className="color-selector">
                {colors.map(color => (
                    <span key={color}
                        className={'color-item ' + color + (activeColor === color ? ' active' : '')}
                        onClick={() => handleColorChange(filterId, color)}></span>
                ))}
            </div>
        );
    };

    const sizeOptions = (filterId: string, activeSize: string) => {
        return (
            <div className="size-selector">
                <span className={'size-item normal' + (activeSize === 'normal' ? ' active' : '')}
                    onClick={() => handleSizeChange(filterId, 'normal')}>{'A'}</span>
                <span className={'size-item big' + (activeSize === 'big' ? ' active' : '')}
                    onClick={() => handleSizeChange(filterId, 'big')}>{'A'}</span>
                <span className={'size-item huge' + (activeSize === 'huge' ? ' active' : '')}
                    onClick={() => handleSizeChange(filterId, 'huge')}>{'A'}</span>
            </div>
        );
    };

    const getFilterLabel = (category: string, option: string): string => {
        const labels: Record<string, Record<string, string>> = {
            pragmatics: {
                'all': t("All pragmatic expressions"),
                'suggesting': t("Suggesting"),
                'thanking': t("Thanking"),
                'requesting': t("Requesting"),
                'agreeing-disagreeing': t("Agreeing/Disagreeing"),
                'showing-interest': t("Showing interest"),
                'apologising': t("Apologising"),
                'giving-an-opinion': t("Giving an opinion"),
                'greeting': t("Greeting"),
            },
            phrasalVerbs: {
                'all': t("Phrasal verbs"),
            },
            nouns: {
                'all': t("All nouns"),
                'irregular': t("Irregular plurals"),
            },
            adjectives: {
                'all': t("All adjectives"),
                'adjectives-comparative': t("Comparative adjectives"),
                'adjectives-superlative': t("Superlative adjectives"),
            },
            adverbs: {
                'all': t("All adverbs"),
                'adverbs-comparative': t("Comparative adverbs"),
                'adverbs-superlative': t("Superlative adverbs"),
                'adverbs-wh': t("Wh-adverb"),
            },
            verbs: {
                'all': t("All verbs"),
                'verbs-irregular': t("Irregular verbs"),
                'verbs-modal': t("Modal Verbs"),
                'verbs-imperative': t("Imperative"),
                'verbs-phrasal': t("Phrasal verbs"),
                'verbs-tense-Present_Simple': t("Present Simple"),
                'verbs-tense-Present_Continuous': 'Present Continuous',
                'verbs-tense-Present_Perfect': t("Present Perfect"),
                'verbs-tense-Present_Perfect_Continuous': t("Present Perfect Continuous"),
                'verbs-tense-Past_Simple': t("Past Simple"),
                'verbs-tense-Past_Continuous': t("Past Continuous"),
                'verbs-tense-Past_Perfect': t("Past Perfect"),
                'verbs-tense-Past_Perfect_Continuous': t("Past Perfect Continuous"),
                'verbs-tense-Future_Be_Going_To': t("Future 'going to'"),
                'verbs-tense-Future_Simple': t("Future Simple"),
                'verbs-tense-Future_Perfect': t("Future Perfect"),
                'verbs-tense-Future_Perfect_Continuous': t("Future Perfect Continuous"),
                'verbs-tense-Present_Simple_Passive': t("Present Simple Passive"),
                'verbs-tense-Present_Continuous_Passive': t("Present Continuous Passive"),
                'verbs-tense-Present_Perfect_Passive': t("Present Perfect Passive"),
                'verbs-tense-Present_Perfect_Continuous_Passive': t("Present Perfect Continuous Passive"),
                'verbs-tense-Past_Simple_Passive': t("Past Simple Passive"),
                'verbs-tense-Past_Continuous_Passive': t("Past Continuous Passive"),
                'verbs-tense-Past_Perfect_Passive': t("Past Perfect Passive"),
                'verbs-tense-Past_Perfect_Continuous_Passive': t("Past Perfect Continuous Passive"),
                'verbs-tense-Future_Simple_Passive': t("Future Simple Passive"),
                'verbs-tense-Future_Perfect_Passive': t("Future Perfect Passive"),
            },
            prepositions: {
                'all': t("Prepositions"),
            },
            subConjunctions: {
                'all': t("Subordinating Conjunctions"),
            },
            determiners: {
                'all': t("All determiners"),
                'determiners-determiner': t("Determiners"),
                'determiners-wh': t("Wh-determiner"),
            },
            pronouns: {
                'all': t("All pronouns"),
                'pronouns-personal': t("Personal Pronouns"),
                'pronouns-possessive': t("Possessive Pronouns"),
                'pronouns-possessive-wh': t("Possessive wh-pronoun"),
                'pronouns-wh': t("Wh-pronouns"),
            },
            conjunctions: {
                'all': t("Coordinating Conjunctions"),
            },
        };
        const groupNames: Record<string, string> = {
            pragmatics: t("Pragmatic expressions"),
            nouns: t("Nouns"),
            adjectives: t("Adjectives"),
            adverbs: t("Adverbs"),
            verbs: t("Verbs"),
            determiners: t("Determiners"),
            pronouns: t("Pronouns"),
        };
        const label = labels[category]?.[option] || option;
        if (option !== 'all' && groupNames[category]) {
            return groupNames[category] + ' › ' + label;
        }
        return label;
    };

    const getPickerGroups = () => [
        {
            key: 'pragmatics',
            label: t("Pragmatic Expressions"),
            options: [
                { value: 'all', label: t("All pragmatic expressions"), count: nlpCategories.pragmaticsAll },
                { value: 'suggesting', label: t("Suggesting"), count: nlpCategories.pragmaticsSuggesting },
                { value: 'thanking', label: t("Thanking"), count: nlpCategories.pragmaticsThanking },
                { value: 'requesting', label: t("Requesting"), count: nlpCategories.pragmaticsRequesting },
                { value: 'agreeing-disagreeing', label: t("Agreeing/Disagreeing"), count: nlpCategories.pragmaticsAgreeingDisagreeing },
                { value: 'showing-interest', label: t("Showing interest"), count: nlpCategories.pragmaticsShowingInterest },
                { value: 'apologising', label: t("Apologising"), count: nlpCategories.pragmaticsApologising },
                { value: 'giving-an-opinion', label: t("Giving an opinion"), count: nlpCategories.pragmaticsGivingAnOpinion },
                { value: 'greeting', label: t("Greeting"), count: nlpCategories.pragmaticsGreeting },
            ]
        },
        {
            key: 'phrasalVerbs',
            label: t("Phrasal verbs"),
            options: [
                { value: 'all', label: t("Phrasal verbs"), count: nlpCategories.phrasalVerbs },
            ]
        },
        {
            key: 'nouns',
            label: t("Nouns"),
            options: [
                { value: 'all', label: t("All nouns"), count: nlpCategories.nounsAll },
                { value: 'irregular', label: t("Irregular plurals"), count: nlpCategories.nounsIrregular },
            ]
        },
        {
            key: 'adjectives',
            label: t("Adjectives"),
            options: [
                { value: 'all', label: t("All adjectives"), count: nlpCategories.adjectivesAll },
                { value: 'adjectives-comparative', label: t("Comparative adjectives"), count: nlpCategories.adjectivesComparative },
                { value: 'adjectives-superlative', label: t("Superlative adjectives"), count: nlpCategories.adjectivesSuperlative },
            ]
        },
        {
            key: 'adverbs',
            label: t("Adverbs"),
            options: [
                { value: 'all', label: t("All adverbs"), count: nlpCategories.adverbsAll },
                { value: 'adverbs-comparative', label: t("Comparative adverbs"), count: nlpCategories.adverbsComparative },
                { value: 'adverbs-superlative', label: t("Superlative adverbs"), count: nlpCategories.adverbsSuperlative },
                { value: 'adverbs-wh', label: t("Wh-adverb"), count: nlpCategories.adverbsWh },
            ]
        },
        {
            key: 'verbs',
            label: t("Verbs"),
            options: [
                { value: 'all', label: t("All verbs"), count: nlpCategories.verbsAll },
                { value: 'verbs-irregular', label: t("Irregular verbs"), count: nlpCategories.verbsIrregular },
                { value: 'verbs-modal', label: t("Modal Verbs"), count: nlpCategories.verbsTenseModal },
                { value: 'verbs-imperative', label: t("Imperative"), count: nlpCategories.verbsTenseImperative },
                { value: 'verbs-phrasal', label: t("Phrasal verbs"), count: nlpCategories.phrasalVerbs },
            ],
            subgroups: [
                {
                    key: 'verbs-tense',
                    label: t("Verb Tense"),
                    count: nlpCategories.verbsTenseAll,
                    sections: [
                        { title: t("Active"), options: [
                            { value: 'verbs-tense-Present_Simple', label: t("Present Simple"), count: nlpCategories.verbsTensePresentSimple },
                            { value: 'verbs-tense-Present_Continuous', label: 'Present Continuous', count: nlpCategories.verbsTensePresentContinuous },
                            { value: 'verbs-tense-Present_Perfect', label: t("Present Perfect"), count: nlpCategories.verbsTensePresentPerfect },
                            { value: 'verbs-tense-Present_Perfect_Continuous', label: t("Present Perfect Continuous"), count: nlpCategories.verbsTensePresentPerfectContinuous },
                            { value: 'verbs-tense-Past_Simple', label: t("Past Simple"), count: nlpCategories.verbsTensePastSimple },
                            { value: 'verbs-tense-Past_Continuous', label: t("Past Continuous"), count: nlpCategories.verbsTensePastContinuous },
                            { value: 'verbs-tense-Past_Perfect', label: t("Past Perfect"), count: nlpCategories.verbsTensePastPerfect },
                            { value: 'verbs-tense-Past_Perfect_Continuous', label: t("Past Perfect Continuous"), count: nlpCategories.verbsTensePastPerfectContinuous },
                            { value: 'verbs-tense-Future_Be_Going_To', label: t("Future 'going to'"), count: nlpCategories.verbsTenseFutureBeGoingTo },
                            { value: 'verbs-tense-Future_Simple', label: t("Future Simple"), count: nlpCategories.verbsTenseFutureSimple },
                            { value: 'verbs-tense-Future_Perfect', label: t("Future Perfect"), count: nlpCategories.verbsTenseFuturePerfect },
                            { value: 'verbs-tense-Future_Perfect_Continuous', label: t("Future Perfect Continuous"), count: nlpCategories.verbsTenseFuturePerfectContinuous },
                        ]},
                        { title: t("Passive"), options: [
                            { value: 'verbs-tense-Present_Simple_Passive', label: t("Present Simple Passive"), count: nlpCategories.verbsTensePresentSimplePassive },
                            { value: 'verbs-tense-Present_Continuous_Passive', label: t("Present Continuous Passive"), count: nlpCategories.verbsTensePresentContinuousPassive },
                            { value: 'verbs-tense-Present_Perfect_Passive', label: t("Present Perfect Passive"), count: nlpCategories.verbsTensePresentPerfectPassive },
                            { value: 'verbs-tense-Present_Perfect_Continuous_Passive', label: t("Present Perfect Continuous Passive"), count: nlpCategories.verbsTensePresentPerfectContinuousPassive },
                            { value: 'verbs-tense-Past_Simple_Passive', label: t("Past Simple Passive"), count: nlpCategories.verbsTensePastSimplePassive },
                            { value: 'verbs-tense-Past_Continuous_Passive', label: t("Past Continuous Passive"), count: nlpCategories.verbsTensePastContinuousPassive },
                            { value: 'verbs-tense-Past_Perfect_Passive', label: t("Past Perfect Passive"), count: nlpCategories.verbsTensePastPerfectPassive },
                            { value: 'verbs-tense-Past_Perfect_Continuous_Passive', label: t("Past Perfect Continuous Passive"), count: nlpCategories.verbsTensePastPerfectContinuousPassive },
                            { value: 'verbs-tense-Future_Simple_Passive', label: t("Future Simple Passive"), count: nlpCategories.verbsTenseFutureSimplePassive },
                            { value: 'verbs-tense-Future_Perfect_Passive', label: t("Future Perfect Passive"), count: nlpCategories.verbsTenseFuturePerfectPassive },
                        ]},
                    ]
                }
            ]
        },
        {
            key: 'prepositions',
            label: t("Prepositions"),
            options: [
                { value: 'all', label: t("Prepositions"), count: nlpCategories.prepositionsAll },
            ]
        },
        {
            key: 'subConjunctions',
            label: t("Subordinating Conjunctions"),
            options: [
                { value: 'all', label: t("Subordinating Conjunctions"), count: nlpCategories.subConjunctionsAll },
            ]
        },
        {
            key: 'determiners',
            label: t("Determiners"),
            options: [
                { value: 'all', label: t("All determiners"), count: nlpCategories.determinersAll },
                { value: 'determiners-determiner', label: t("Determiners"), count: nlpCategories.determinersDeterminer },
                { value: 'determiners-wh', label: t("Wh-determiner"), count: nlpCategories.determinersWh },
            ]
        },
        {
            key: 'pronouns',
            label: t("Pronouns"),
            options: [
                { value: 'all', label: t("All pronouns"), count: nlpCategories.pronounsAll },
                { value: 'pronouns-personal', label: t("Personal Pronouns"), count: nlpCategories.pronounsPersonal },
                { value: 'pronouns-possessive', label: t("Possessive Pronouns"), count: nlpCategories.pronounsPossessive },
                { value: 'pronouns-possessive-wh', label: t("Possessive wh-pronoun"), count: nlpCategories.pronounsPossessiveWh },
                { value: 'pronouns-wh', label: t("Wh-pronouns"), count: nlpCategories.pronounsWh },
            ]
        },
        {
            key: 'conjunctions',
            label: t("Coordinating Conjunctions"),
            options: [
                { value: 'all', label: t("Coordinating Conjunctions"), count: nlpCategories.conjunctionsAll },
            ]
        },
    ];

    const renderFilterCard = (filter: FilterItem) => {
        return (
            <div className="word-category" key={filter.id}>
                <div className="word-category-switch active" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{getFilterLabel(filter.category, filter.option)}</span>
                    <span
                        onClick={() => removeFilter(filter.id)}
                        style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', lineHeight: 1, padding: '0 4px' }}
                        title={t("Remove")}
                    >&times;</span>
                </div>
                <div className="options">
                    <div className="format-options">
                        {colorOptions(filter.id, filter.color)}
                        {sizeOptions(filter.id, filter.size)}
                        <div className="other-formats">
                            <span className={'bold' + (filter.bold ? ' active' : '')}
                                onClick={() => handleBoldToggle(filter.id)}></span>
                            <span className={'italic' + (filter.italic ? ' active' : '')}
                                onClick={() => handleItalicToggle(filter.id)}></span>
                            <span className={'underline' + (filter.underline ? ' active' : '')}
                                onClick={() => handleUnderlineToggle(filter.id)}></span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderFilterPicker = () => {
        const groups = getPickerGroups();
        return (
            <div className="filter-picker">
                {groups.map((group: any) => (
                    <div key={group.key} className="filter-picker-group">
                        <div
                            className={'filter-picker-group-header' + (expandedPickerGroup === group.key ? ' expanded' : '')}
                            onClick={() => { setExpandedPickerGroup(expandedPickerGroup === group.key ? null : group.key); setExpandedPickerSubgroup(null); }}
                        >
                            <span className="filter-picker-arrow">{expandedPickerGroup === group.key ? '\u25BC' : '\u25B6'}</span>
                            {group.label}
                        </div>
                        {expandedPickerGroup === group.key &&
                            <div className="filter-picker-options">
                                {group.options.map((opt: any) => (
                                    <div
                                        key={opt.value}
                                        className="filter-picker-option"
                                        onClick={() => addFilter(group.key, opt.value)}
                                    >
                                        {opt.label} ({opt.count || 0})
                                    </div>
                                ))}
                                {group.subgroups && group.subgroups.map((subgroup: any) => (
                                    <div key={subgroup.key} className="filter-picker-subgroup">
                                        <div
                                            className={'filter-picker-subgroup-header' + (expandedPickerSubgroup === subgroup.key ? ' expanded' : '')}
                                            onClick={() => setExpandedPickerSubgroup(expandedPickerSubgroup === subgroup.key ? null : subgroup.key)}
                                        >
                                            <span className="filter-picker-arrow">{expandedPickerSubgroup === subgroup.key ? '\u25BC' : '\u25B6'}</span>
                                            {subgroup.label} ({subgroup.count || 0})
                                        </div>
                                        {expandedPickerSubgroup === subgroup.key &&
                                            <div className="filter-picker-suboptions">
                                                {subgroup.sections.map((section: any) => (
                                                    <div key={section.title}>
                                                        <div className="filter-picker-section-title">{section.title}</div>
                                                        {section.options.map((opt: any) => (
                                                            <div
                                                                key={opt.value}
                                                                className="filter-picker-option"
                                                                onClick={() => addFilter(group.key, opt.value)}
                                                            >
                                                                {opt.label} ({opt.count || 0})
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                ))}
            </div>
        );
    };

    // --- useEffect for NLP categories (unchanged logic from original) ---

    useEffect(() => {
        if (props.analyzedText.main && props.analyzedText.main.length == 0 &&
            props.analyzedText.countPhrasals && props.analyzedText.countPhrasals.length == 0 &&
            props.analyzedText.countTense && props.analyzedText.countTense.length == 0 &&
            props.analyzedText.countPragmatics && props.analyzedText.countPragmatics.length == 0) {
            setProcessingEmpty(true);
        } else {
            setProcessingEmpty(false);
            const newNlpCategories = {
                pragmaticsAll: getPragmaticFrequency(props.analyzedText.countPragmatics, 'Suggestion_Direct') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Suggestion_Conventionalised') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Suggestion_Indirect') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Thanking_Informal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Thanking_Conventionalised') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Thanking_Formal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Request_Direct') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Request_Conventionalised') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Request_Indirect') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Agreeing/Disagreeing_Informal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Agreeing/Disagreeing_Formal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Showing_Interest_Informal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Showing_Interest_Formal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Apologising_Conventionalised') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Apologising_Informal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Apologising_Formal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Giving_Opinion_Conventionalised') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Giving_Opinion_Formal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Greeting_Informal') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Greeting_Conventionalised') +
                    getPragmaticFrequency(props.analyzedText.countPragmatics, 'Greeting_Formal'),
                pragmaticsSuggesting: getPragmaticFrequency(props.analyzedText.countPragmatics, 'Suggestion_Direct') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Suggestion_Conventionalised') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Suggestion_Indirect'),
                pragmaticsThanking: getPragmaticFrequency(props.analyzedText.countPragmatics, 'Thanking_Informal') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Thanking_Conventionalised') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Thanking_Formal'),
                pragmaticsRequesting: getPragmaticFrequency(props.analyzedText.countPragmatics, 'Request_Direct') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Request_Conventionalised') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Request_Indirect'),
                pragmaticsAgreeingDisagreeing: getPragmaticFrequency(props.analyzedText.countPragmatics, 'Agreeing/Disagreeing_Informal') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Agreeing/Disagreeing_Formal'),
                pragmaticsShowingInterest: getPragmaticFrequency(props.analyzedText.countPragmatics, 'Showing_Interest_Informal') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Showing_Interest_Formal'),
                pragmaticsApologising: getPragmaticFrequency(props.analyzedText.countPragmatics, 'Apologising_Conventionalised') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Apologising_Informal') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Apologising_Formal'),
                pragmaticsGivingAnOpinion: getPragmaticFrequency(props.analyzedText.countPragmatics, 'Giving_Opinion_Conventionalised') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Giving_Opinion_Formal'),
                pragmaticsGreeting: getPragmaticFrequency(props.analyzedText.countPragmatics, 'Greeting_Informal') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Greeting_Conventionalised') + getPragmaticFrequency(props.analyzedText.countPragmatics, 'Greeting_Formal'),
                phrasalVerbs: getPhrasalFrequency(props.analyzedText.countPhrasals, 'PhrV'),
                nounsAll: props.analyzedText.main.filter((c: any) => ['NN', 'NNP', 'NNS', 'NNPS', 'IrrPl'].includes(c.xpos)).length,
                nounsIrregular: props.analyzedText.main.filter((c: any) => 'IrrPl' == c.xpos).length,
                adjectivesAll: props.analyzedText.main.filter((c: any) => ['JJ', 'JJR', 'JJS', 'JJR', 'JJS'].includes(c.xpos)).length,
                adjectivesSuperlative: props.analyzedText.main.filter((c: any) => 'JJS' == c.xpos).length,
                adjectivesComparative: props.analyzedText.main.filter((c: any) => 'JJR' == c.xpos).length,
                adverbsAll: props.analyzedText.main.filter((c: any) => ['RB', 'RBR', 'RBS', 'WRB'].includes(c.xpos)).length,
                adverbsComparative: props.analyzedText.main.filter((c: any) => 'JJR' == c.xpos).length,
                adverbsSuperlative: props.analyzedText.main.filter((c: any) => 'RBR' == c.xpos).length,
                adverbsWh: props.analyzedText.main.filter((c: any) => 'WRB' == c.xpos).length,
                verbsAll: props.analyzedText.main.filter((c: any) => ['VB', 'VBZ', 'VBD', 'VBN', 'MD', 'VBG', 'VBP'].includes(c.xpos)).length,
                verbsIrregular: props.analyzedText.main.filter((c: any) => ['VB', 'VBZ', 'VBD', 'VBN', 'MD', 'VBG', 'VPB'].includes(c.xpos) && c.irregularform == 'IrrV').length,
                verbsTenseAll: getTenseFrequency(props.analyzedText.countTense, 'Present Simple') +
                    getTenseFrequency(props.analyzedText.countTense, 'Present Continuous') +
                    getTenseFrequency(props.analyzedText.countTense, 'Present Perfect') +
                    getTenseFrequency(props.analyzedText.countTense, 'Present Perfect Continuous') +
                    getTenseFrequency(props.analyzedText.countTense, 'Past Simple') +
                    getTenseFrequency(props.analyzedText.countTense, 'Past Continuous') +
                    getTenseFrequency(props.analyzedText.countTense, 'Past Perfect') +
                    getTenseFrequency(props.analyzedText.countTense, 'Past Perfect Continuous') +
                    getTenseFrequency(props.analyzedText.countTense, 'Future Be Going To') +
                    getTenseFrequency(props.analyzedText.countTense, 'Future Simple') +
                    getTenseFrequency(props.analyzedText.countTense, 'Future Perfect') +
                    getTenseFrequency(props.analyzedText.countTense, 'Future Perfect Continuous') +
                    getTenseFrequency(props.analyzedText.countTense, 'Present Simple Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Present Continuous Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Present Perfect Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Present Perfect Continuous Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Past Simple Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Past Continuous Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Past Perfect Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Past Perfect Continuous Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Future Simple Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Future Perfect Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Modal Verb') +
                    getTenseFrequency(props.analyzedText.countTense, 'Modal Perfect') +
                    getTenseFrequency(props.analyzedText.countTense, 'Modal Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Modal Perfect Passive') +
                    getTenseFrequency(props.analyzedText.countTense, 'Imperative'),
                verbsTensePresentSimple: getTenseFrequency(props.analyzedText.countTense, 'Present Simple'),
                verbsTensePresentContinuous: getTenseFrequency(props.analyzedText.countTense, 'Present Continuous'),
                verbsTensePresentPerfect: getTenseFrequency(props.analyzedText.countTense, 'Present Perfect'),
                verbsTensePresentPerfectContinuous: getTenseFrequency(props.analyzedText.countTense, 'Present Perfect Continuous'),
                verbsTensePastSimple: getTenseFrequency(props.analyzedText.countTense, 'Past Simple'),
                verbsTensePastContinuous: getTenseFrequency(props.analyzedText.countTense, 'Past Continuous'),
                verbsTensePastPerfect: getTenseFrequency(props.analyzedText.countTense, 'Past Perfect'),
                verbsTensePastPerfectContinuous: getTenseFrequency(props.analyzedText.countTense, 'Past Perfect Continuous'),
                verbsTenseFutureBeGoingTo: getTenseFrequency(props.analyzedText.countTense, 'Future Be Going To'),
                verbsTenseFutureSimple: getTenseFrequency(props.analyzedText.countTense, 'Future Simple'),
                verbsTenseFuturePerfect: getTenseFrequency(props.analyzedText.countTense, 'Future Perfect'),
                verbsTenseFuturePerfectContinuous: getTenseFrequency(props.analyzedText.countTense, 'Future Perfect Continuous'),
                verbsTensePresentSimplePassive: getTenseFrequency(props.analyzedText.countTense, 'Present Simple Passive'),
                verbsTensePresentContinuousPassive: getTenseFrequency(props.analyzedText.countTense, 'Present Continuous Passive'),
                verbsTensePresentPerfectPassive: getTenseFrequency(props.analyzedText.countTense, 'Present Perfect Passive'),
                verbsTensePresentPerfectContinuousPassive: getTenseFrequency(props.analyzedText.countTense, 'Present Perfect Continuous Passive'),
                verbsTensePastSimplePassive: getTenseFrequency(props.analyzedText.countTense, 'Past Simple Passive'),
                verbsTensePastContinuousPassive: getTenseFrequency(props.analyzedText.countTense, 'Past Continuous Passive'),
                verbsTensePastPerfectPassive: getTenseFrequency(props.analyzedText.countTense, 'Past Perfect Passive'),
                verbsTensePastPerfectContinuousPassive: getTenseFrequency(props.analyzedText.countTense, 'Past Perfect Continuous Passive'),
                verbsTenseFutureSimplePassive: getTenseFrequency(props.analyzedText.countTense, 'Future Simple Passive'),
                verbsTenseFuturePerfectPassive: getTenseFrequency(props.analyzedText.countTense, 'Future Perfect Passive'),
                verbsTenseModal: getTenseFrequency(props.analyzedText.countTense, 'Modal Verb') + getTenseFrequency(props.analyzedText.countTense, 'Modal Perfect') + getTenseFrequency(props.analyzedText.countTense, 'Modal Passive') + getTenseFrequency(props.analyzedText.countTense, 'Modal Perfect Passive'),
                verbsTenseImperative: getTenseFrequency(props.analyzedText.countTense, 'Imperative'),
                prepositionsAll: props.analyzedText.main.filter((c: any) => 'IN' == c.xpos && c.upos == 'ADP').length,
                subConjunctionsAll: props.analyzedText.main.filter((c: any) => 'IN' == c.xpos && c.upos == 'SCONJ').length,
                determinersAll: props.analyzedText.main.filter((c: any) => ['DT', 'PDT', 'WDT'].includes(c.xpos)).length,
                determinersDeterminer: props.analyzedText.main.filter((c: any) => ['DT', 'PDT'].includes(c.xpos)).length,
                determinersWh: props.analyzedText.main.filter((c: any) => 'WDT' == c.xpos).length,
                pronounsAll: props.analyzedText.main.filter((c: any) => ['PRP', 'PRP$', 'WP$', 'WP'].includes(c.xpos)).length,
                pronounsPersonal: props.analyzedText.main.filter((c: any) => 'PRP' == c.xpos).length,
                pronounsPossessive: props.analyzedText.main.filter((c: any) => 'PRP$' == c.xpos).length,
                pronounsPossessiveWh: props.analyzedText.main.filter((c: any) => 'WP$' == c.xpos).length,
                pronounsWh: props.analyzedText.main.filter((c: any) => 'WP' == c.xpos).length,
                conjunctionsAll: props.analyzedText.main.filter((c: any) => 'CC' == c.xpos).length,
            };
            setNlpCategories(newNlpCategories);
            formatText(props.analyzedText.main, []);
        }
    }, []);

    // --- Render ---

    return (
        <div className="processed-text-container">
            { processingEmpty &&
                <div className="empty-processed-text">
                    <Alert key={ 'warning' } variant={ 'warning' }>
                        { t("Sorry! The result of the text analysis is either empty or some errors were found. Revise and change your text and try it again.") }
                    </Alert>
                </div>
            }
            { !processingEmpty &&
                <div className="text-filters">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => { setShowPicker(!showPicker); setExpandedPickerGroup(null); setExpandedPickerSubgroup(null); }}
                        style={{ width: '100%', marginBottom: '10px' }}
                    >
                        { showPicker ? t("Cancel") : '+ ' + t("Add formatting rule") }
                    </Button>

                    { showPicker && renderFilterPicker() }

                    { filters.map(filter => renderFilterCard(filter)) }
                </div>
            }
            <div className="text-presentation">
                <div className="text-formatted">
                    { parse(allText)}
                </div>
            </div>
        </div>
    );
}
